/**
 * Pure helpers for the gutschrift-check manual line-link tool (#1051).
 *
 * Split out from ``MatchGraph.tsx`` so a Vitest test can pin the
 * validation contract without mounting ``<ReactFlow>``. ``MatchGraph``
 * imports + wires these into ``isValidConnection`` / ``onConnect`` /
 * ``onEdgesDelete``.
 */
import type { MatchGroup } from '../../../hooks/useMatchGroups';

export type LineSide = 'delivery' | 'settlement';

export interface LineSideEntry {
  side: LineSide;
  groupId: string;
}

export type LineSideMap = ReadonlyMap<string, LineSideEntry>;

/**
 * Build a ``lineId → {side, groupId}`` lookup from the canvas's
 * ``MatchGroup[]``. Used by ``isValidConnection`` to reject
 * cross-group connections (the materialiser groups by content-
 * addressed event set; merging across groups is undefined) and by
 * ``normaliseConnectionForLink`` to figure out which dropped-on
 * endpoint is delivery vs settlement (the user can drag in either
 * direction).
 */
export function buildLineSideMap(groups: readonly MatchGroup[]): LineSideMap {
  const map = new Map<string, LineSideEntry>();
  for (const g of groups) {
    for (const dl of g.deliveryLines) {
      map.set(dl.lineId, { side: 'delivery', groupId: g.id });
    }
    for (const sl of g.settlementLines) {
      map.set(sl.lineId, { side: 'settlement', groupId: g.id });
    }
  }
  return map;
}

/**
 * Narrow connection shape — mirrors xyflow ``Connection`` without
 * importing the type so this module stays a pure-data unit and Vitest
 * doesn't have to pull in @xyflow/react.
 */
export interface ConnectionLike {
  source: string | null;
  target: string | null;
  sourceHandle: string | null;
  targetHandle: string | null;
}

/**
 * Line-spot handles are minted from the ``event_line`` entity id
 * ``f"{event_id}:{idx}"`` so they always contain a colon. Named
 * handles on the card chrome (``title-source``, ``referenz-source``,
 * ``bestellung-source``) never contain a colon — that's the
 * discriminator ``isValidConnection`` uses to reject non-line drags.
 */
export function isLineSpotHandle(handleId: string | null | undefined): boolean {
  return typeof handleId === 'string' && handleId.includes(':');
}

/**
 * The drag-to-connect validation predicate. Rejects:
 *
 * * connections that touch a non-line handle (card chrome);
 * * self-loops (same handle on both ends);
 * * cross-card connections where either endpoint is unknown
 *   (defensive — shouldn't happen in practice because the line-side
 *   map covers every rendered card);
 * * cross-group connections (LS line from group A → GS line from
 *   group B). The materialiser is content-addressed by event set;
 *   merging across groups is undefined.
 * * same-side connections (LS↔LS or GS↔GS) — the data model is a
 *   bipartite LS↔GS graph.
 */
export function isValidLineConnection(
  connection: ConnectionLike,
  lineSideMap: LineSideMap,
): boolean {
  return classifyLineConnection(connection, lineSideMap) === 'valid';
}

/**
 * Why a drag-to-connect attempt is valid or invalid (#1391 11a). The
 * pre-#1391 handler surfaced ONE generic "Anschluss muss zwischen einer
 * Lieferscheinzeile und einer Gutschriftzeile liegen" toast for every
 * reject, which confused operators ("Ich kann keine Verbindung machen —
 * komische Warnung"). Classifying the reason lets ``buildHandleConnect``
 * / ``buildHandleConnectEnd`` pick copy that states WHY:
 *
 *  * ``valid`` — a real LS-line ↔ GS-line link (accepted);
 *  * ``non_line`` — an endpoint is card chrome (title / Referenz /
 *    Bestellung handle), not a Waren line;
 *  * ``same_side`` — both endpoints are Lieferschein lines, or both are
 *    Gutschrift lines (the data model is a bipartite LS↔GS graph);
 *  * ``cross_group`` — the two lines belong to different match groups
 *    (merging across groups is undefined);
 *  * ``unknown_endpoint`` — a self-loop, or an endpoint the side map
 *    doesn't cover (defensive — shouldn't happen in practice);
 *  * ``chrome_same_group`` — an endpoint landed on card CHROME (header /
 *    Referenz / Bestellung anchor) and the two CARDS involved are a
 *    Lieferschein and a Gutschrift in the same match group (#3237).
 *    Reported as its own verdict so the copy can point at the Waren-line
 *    spots: the link the operator is reaching for IS expressible, just
 *    not from where they released.
 */
export type LineConnectionVerdict =
  | 'valid'
  | 'non_line'
  | 'chrome_same_group'
  | 'same_side'
  | 'cross_group'
  | 'unknown_endpoint';

/**
 * Every named handle on the card chrome (#3237). These are edge anchors
 * for the dashed REF / Bestellung / Abrechnung arrows — invisible, on the
 * card edge, and (unlike a Waren-line spot) carrying no
 * ``{event_id}:{idx}`` id, so they can never form a link.
 *
 * Kept as an explicit allowlist rather than inferring "chrome" from
 * "isn't a line spot", so a future handle type that is neither can't
 * silently inherit the ``chrome_same_group`` copy. Pinned against what
 * ``LineRow`` actually renders by
 * ``__tests__/LineRow.chromeHandles.test.tsx``.
 */
export const CHROME_HANDLE_IDS = [
  'title-target',
  'title-source',
  'referenz-source',
  'bestellung-from-gs',
  'bestellung-source',
] as const;

export function isChromeHandle(handleId: string | null | undefined): boolean {
  return (
    typeof handleId === 'string' &&
    (CHROME_HANDLE_IDS as readonly string[]).includes(handleId)
  );
}

/**
 * Split the pre-#3237 flat ``non_line`` verdict in two (#3237).
 *
 * The reported bug is a drag towards the Gutschrift card that ends on
 * card chrome — "connect the Lieferschein to the Gutschrift". The chrome
 * handles that face the other document at header height (``title-source``
 * on the LS right edge at ``top: 22``, ``referenz-source`` /
 * ``bestellung-from-gs`` on the GS left edge) are exactly the ones that
 * can never form a link, and ``getClosestHandle`` snaps to them by
 * distance alone, so the natural gesture is steered straight into the
 * reject.
 *
 * When the two CARDS are an LS/GS pair in ONE match group, the operator's
 * intent is satisfiable today — the backend's ``line-link`` primitive is
 * same-group and line-level — so the toast should say where to drag
 * instead of only what not to touch.
 *
 * **Which drops actually reach here.** Since #3237 guards the chrome
 * handles with ``isConnectableStart: false``, a drag can no longer BEGIN
 * on chrome, so a chrome→chrome drop is unreachable in the real canvas.
 * The reachable — and much more common — miss is the operator starting on
 * the correct Waren-line dot and RELEASING over the other card's header
 * or Ref row. That is a half-chrome drop, and it is the case this verdict
 * exists to serve: they picked the right two documents and the wrong
 * spot. Chrome→chrome stays covered as defence-in-depth (click-connect,
 * a future affordance, a changed xyflow contract).
 *
 * Everything else keeps ``non_line``:
 *
 *  * an endpoint that is neither a Waren line nor a KNOWN chrome handle;
 *  * a drop across two match groups, or between two cards on the same
 *    side — no same-group line pair exists to point at;
 *  * any call site that hasn't wired ``cardSideMap`` (the map is optional
 *    so ``isValidConnection`` and older tests keep their 2-arg contract).
 */
function classifyChromeDrop(
  connection: ConnectionLike,
  cardSideMap?: LineSideMap,
): 'chrome_same_group' | 'non_line' {
  // Each endpoint must be either a real Waren line or a known chrome
  // handle. An unrecognised handle id gets the blunt copy — pointing at
  // the line spots would be a guess.
  for (const handleId of [connection.sourceHandle, connection.targetHandle]) {
    if (!isLineSpotHandle(handleId) && !isChromeHandle(handleId)) {
      return 'non_line';
    }
  }
  if (!cardSideMap) return 'non_line';
  const a = connection.source ? cardSideMap.get(connection.source) : undefined;
  const b = connection.target ? cardSideMap.get(connection.target) : undefined;
  if (!a || !b) return 'non_line';
  if (a.side === b.side) return 'non_line';
  if (a.groupId !== b.groupId) return 'non_line';
  return 'chrome_same_group';
}

export function classifyLineConnection(
  connection: ConnectionLike,
  lineSideMap: LineSideMap,
  /** #3237 — ``nodeId → {side, groupId}`` for the rendered LS/GS cards.
   *  Optional: only the toast path needs it, and omitting it collapses
   *  the new verdict back onto the pre-#3237 ``non_line``. */
  cardSideMap?: LineSideMap,
): LineConnectionVerdict {
  if (!isLineSpotHandle(connection.sourceHandle) || !isLineSpotHandle(connection.targetHandle)) {
    return classifyChromeDrop(connection, cardSideMap);
  }
  const sourceHandle = connection.sourceHandle as string;
  const targetHandle = connection.targetHandle as string;
  if (sourceHandle === targetHandle) return 'unknown_endpoint';
  const a = lineSideMap.get(sourceHandle);
  const b = lineSideMap.get(targetHandle);
  if (!a || !b) return 'unknown_endpoint';
  if (a.side === b.side) return 'same_side';
  if (a.groupId !== b.groupId) return 'cross_group';
  return 'valid';
}

/**
 * Normalise a connection ``{sourceHandle, targetHandle}`` into the
 * backend's ``{delivery_line_id, settlement_line_id}`` order. Returns
 * ``null`` if the connection isn't a valid line-link (caller should
 * already have run ``isValidLineConnection`` first; this is
 * defence-in-depth).
 *
 * The user can drag LS→GS or GS→LS; the backend always takes
 * delivery first, settlement second, so the FE inspects each
 * endpoint's side from the lineSideMap.
 */
export function normaliseConnectionForLink(
  connection: ConnectionLike,
  lineSideMap: LineSideMap,
): { delivery_line_id: string; settlement_line_id: string } | null {
  if (!isLineSpotHandle(connection.sourceHandle) || !isLineSpotHandle(connection.targetHandle)) {
    return null;
  }
  const sourceHandle = connection.sourceHandle as string;
  const targetHandle = connection.targetHandle as string;
  const a = lineSideMap.get(sourceHandle);
  const b = lineSideMap.get(targetHandle);
  if (!a || !b) return null;
  if (a.side === b.side) return null;
  if (a.side === 'delivery') {
    return { delivery_line_id: sourceHandle, settlement_line_id: targetHandle };
  }
  return { delivery_line_id: targetHandle, settlement_line_id: sourceHandle };
}

/**
 * Narrow edge shape — mirrors the fields ``MatchGraph`` already sets
 * on its built edges (``id`` + ``sourceHandle`` + ``targetHandle``).
 * The ``onEdgesDelete`` handler filters via this predicate to scope
 * the Backspace shortcut to the green matched-line edges only —
 * dashed reference / Abrechnung / stub edges keep ignoring the key.
 */
export interface EdgeLike {
  id?: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  /**
   * HOF-219 — carried so the same narrow shape can be handed to the
   * DOCUMENT-level predicate, which reads ``data`` rather than the handles.
   * ``isManualLineEdge`` below does NOT read it and its scoping is unchanged
   * (AC 14); this widens the input TYPE, not the rule.
   */
  data?: unknown;
}

export function isManualLineEdge(edge: EdgeLike): boolean {
  return isLineSpotHandle(edge.sourceHandle) && isLineSpotHandle(edge.targetHandle);
}

/** The backend's ``/twin/match-groups/line-link`` pair, plus the group it belongs to. */
export interface UnlinkTarget {
  delivery_line_id: string;
  settlement_line_id: string;
  groupId: string;
}

/**
 * HOF-216 — normalise a selected edge into the delivery-first pair the
 * unlink endpoint takes, or ``null`` if this edge carries no unlink.
 *
 * Extracted from ``MatchGraph.handleEdgesDelete``, which inlined exactly this
 * so only the Backspace path could reach it. Three triggers converge on it
 * now (on-edge control, context menu, Backspace/Delete), and ``isManualLineEdge``
 * stays the SINGLE predicate that decides which edges qualify — the dotted
 * grey GS→LS REF/POS edge, the Abrechnung edges and the stubs are refused
 * here, once, for all three (HOF-219 carries the document-level half).
 */
export function normaliseEdgeForUnlink(edge: EdgeLike, lineSideMap: LineSideMap): UnlinkTarget | null {
  if (!isManualLineEdge(edge)) return null;
  const sourceLine = edge.sourceHandle as string;
  const targetLine = edge.targetHandle as string;
  const a = lineSideMap.get(sourceLine);
  const b = lineSideMap.get(targetLine);
  if (!a || !b || a.side === b.side) return null;
  return a.side === 'delivery'
    ? { delivery_line_id: sourceLine, settlement_line_id: targetLine, groupId: a.groupId }
    : { delivery_line_id: targetLine, settlement_line_id: sourceLine, groupId: b.groupId };
}
